import { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { countryFilterMessages } from './country-messages';
import style from './country.module.less';
import MultiSelectDropdown from '../../../../advanced-search/components/multi-select-dropdown';
import { useSortedTranslatedCountries } from '../../../../hooks/use-sorted-translated-countries';
import { OptionFormTypes } from '../../../../services/filtering/filter-form-render-service/option-form-types';

type Country = {
  code: string;
  name: string;
  displayName: string;
};

type CountryOption = {
  id: string;
  value: string;
  component: React.ReactNode;
};

type CriterionArgumentOption = {
  value: string;
  displayName: string;
};

type SelectedFilterBy = {
  id: string;
  displayName: React.ReactNode;
  type: string;
  filterValue: string;
};

type CriteriaInformation = {
  criterionArgumentOptions: Array<CriterionArgumentOption>;
  selectedFilterBy: SelectedFilterBy;
};

type ExistingFilter = {
  criterionArgumentValue: string;
};

type CriteriaArgument = {
  value: string;
};

type CriterionOperator = {
  value: string;
};

type ExistingFiltersHelpers = {
  existingFilters: Array<ExistingFilter>;
  onApplyFilter: (filterBy: SelectedFilterBy, operator: CriterionOperator, argument: CriteriaArgument) => void;
  onRemoveFilter: (filter: ExistingFilter) => void;
};

type CountryFilterComponentsProps = {
  criteriaInformation: CriteriaInformation;
  existingFiltersHelpers: ExistingFiltersHelpers;
};

const CountryFilterComponent = ({ criteriaInformation, existingFiltersHelpers }: CountryFilterComponentsProps) => {
  const countries = useSortedTranslatedCountries({ labelProp: 'displayName' }) as Country[];
  const { onApplyFilter, onRemoveFilter, existingFilters } = existingFiltersHelpers;
  const { selectedFilterBy } = criteriaInformation;

  const countriesFilter = useMemo<CountryOption[]>(
    () =>
      countries.map((country) => ({
        id: country.code,
        value: country.code,
        component: country.displayName,
      })),
    [countries],
  );

  const selectedCountries = useMemo<CountryOption[]>(
    () =>
      existingFilters
        .map((filter) => countriesFilter.find((country) => country.value === filter.criterionArgumentValue))
        .filter((country): country is CountryOption => country !== undefined),
    [existingFilters, countriesFilter],
  );

  const handleCountryChange = useCallback(
    (country: CountryOption) => {
      const existingFilter = existingFilters.find((filter) => filter.criterionArgumentValue === country.value);

      if (existingFilter) {
        onRemoveFilter(existingFilter);
      } else {
        const criteriaArgument = { value: country.value };
        const containsCriterionOperator = { value: 'contains' };
        onApplyFilter(selectedFilterBy, containsCriterionOperator, criteriaArgument);
      }
    },
    [existingFilters, onApplyFilter, onRemoveFilter, selectedFilterBy],
  );

  const getLabel = () => {
    if (selectedCountries.length === 0) {
      return <FormattedMessage {...countryFilterMessages.selectSupplier} />;
    }

    if (selectedCountries.length === 1 && selectedCountries[0]) {
      return selectedCountries[0].component;
    }

    return <FormattedMessage {...countryFilterMessages.multipleSelected} />;
  };

  return (
    <div
      className={style['country-filter-container']}
      onKeyDown={(e) => e.stopPropagation()}
      onKeyUp={(e) => e.stopPropagation()}
      role='presentation'
    >
      <MultiSelectDropdown
        label={getLabel()}
        onOptionClicked={handleCountryChange}
        options={countriesFilter}
        selectedOptions={selectedCountries}
        shouldSendFocusToInput={false}
      />
    </div>
  );
};

const countryFilterDisplayName = <FormattedMessage {...countryFilterMessages.filterHeader} />;

/**
 * Filter configuration for Country field
 * Allows filtering suppliers by their country using country codes
 * Supports multiple country selection
 */
export const countryFilterBy = {
  id: 'country',
  type: OptionFormTypes.TYPE_IS_OPTIONS,
  filterValue: 'countryCode',
  displayName: countryFilterDisplayName,
  expectsArgumentAsArray: true,
  shouldHideActionButtons: true,
  render: (criteriaInformation: CriteriaInformation, existingFiltersHelpers: ExistingFiltersHelpers) => {
    return (
      <CountryFilterComponent
        key='country-filter-stable'
        criteriaInformation={criteriaInformation}
        existingFiltersHelpers={existingFiltersHelpers}
      />
    );
  },
};
